import React, { useState } from 'react';
import { UserPointsData } from '../../services/pointsAdminService';

interface PlanManagementProps {
  user: UserPointsData;
  onPlanChange: (newPlan: string) => void;
  loading: boolean;
}

const PlanManagement: React.FC<PlanManagementProps> = ({ 
  user, 
  onPlanChange, 
  loading 
}) => {
  const [selectedPlan, setSelectedPlan] = useState(user.plan);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const availablePlans = [
    { id: 'creator', name: 'Creator (Free)', color: '#6b7280' },
    { id: 'creator_lite', name: 'Creator Lite', color: '#06b6d4' },
    { id: 'creator_pro', name: 'Creator Pro', color: '#8b5cf6' },
    { id: 'creator_studio', name: 'Creator Studio', color: '#10b981' },
    { id: 'creator_unlimited', name: 'Creator Unlimited', color: '#f59e0b' }
  ];

  const currentPlan = availablePlans.find(p => p.id === user.plan);
  const newPlan = availablePlans.find(p => p.id === selectedPlan);

  const handlePlanChange = () => {
    if (selectedPlan === user.plan) return;
    setShowConfirmation(true);
  };

  const confirmPlanChange = () => {
    onPlanChange(selectedPlan);
    setShowConfirmation(false);
  };

  const getPlanFeatures = (planId: string) => {
    switch (planId) {
      case 'creator':
        return 'Feature counts: 5 captions, 20 thumbnails, etc.';
      case 'creator_lite':
        return '7,000 points/month';
      case 'creator_pro':
        return '15,000 points/month';
      case 'creator_studio':
        return '30,000 points/month';
      case 'creator_unlimited':
        return 'Unlimited points with daily throttling';
      default:
        return 'Unknown plan';
    }
  };

  return (
    <div style={{ 
      background: 'rgba(15, 23, 42, 0.8)', 
      border: '1px solid rgba(71, 85, 105, 0.3)',
      borderRadius: '12px',
      padding: '1.5rem',
      marginBottom: '1rem'
    }}>
      <h3 style={{ 
        color: '#e5e7eb', 
        marginBottom: '1rem', 
        fontSize: '1.1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        🔄 Plan Management
      </h3>

      {/* Current Plan */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ 
          display: 'block', 
          color: '#9ca3af', 
          fontSize: '0.875rem', 
          marginBottom: '0.5rem' 
        }}>
          Current Plan
        </label>
        <div style={{ 
          padding: '0.75rem',
          background: 'rgba(30, 41, 59, 0.8)',
          border: '1px solid rgba(71, 85, 105, 0.5)',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <span style={{ 
              color: currentPlan?.color || '#9ca3af',
              fontWeight: 'bold'
            }}>
              {currentPlan?.name || user.plan}
            </span>
            <div style={{ 
              color: '#9ca3af', 
              fontSize: '0.75rem', 
              marginTop: '0.25rem' 
            }}>
              {getPlanFeatures(user.plan)}
            </div>
          </div>
        </div>
      </div>

      {/* Plan Selection */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ 
          display: 'block', 
          color: '#9ca3af', 
          fontSize: '0.875rem', 
          marginBottom: '0.5rem' 
        }}>
          Change To Plan
        </label>
        <select
          value={selectedPlan}
          onChange={(e) => setSelectedPlan(e.target.value)}
          disabled={loading}
          style={{
            width: '100%',
            padding: '0.75rem',
            background: 'rgba(30, 41, 59, 0.8)',
            border: '1px solid rgba(71, 85, 105, 0.5)',
            borderRadius: '8px',
            color: '#e5e7eb',
            fontSize: '0.875rem',
            outline: 'none',
            opacity: loading ? 0.6 : 1
          }}
        >
          {availablePlans.map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.name}
            </option>
          ))}
        </select>
        
        {selectedPlan !== user.plan && (
          <div style={{ 
            marginTop: '0.5rem',
            padding: '0.75rem',
            background: 'rgba(6, 182, 212, 0.1)',
            border: '1px solid rgba(6, 182, 212, 0.3)',
            borderRadius: '6px',
            fontSize: '0.875rem'
          }}>
            <div style={{ color: '#06b6d4', marginBottom: '0.5rem' }}>
              Preview: {newPlan?.name}
            </div>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
              {getPlanFeatures(selectedPlan)}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={handlePlanChange}
          disabled={loading || selectedPlan === user.plan}
          style={{
            flex: 1,
            padding: '0.75rem 1.5rem',
            background: loading || selectedPlan === user.plan
              ? 'rgba(71, 85, 105, 0.5)'
              : 'linear-gradient(135deg, #06b6d4, #3b82f6)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: loading || selectedPlan === user.plan ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500',
            opacity: loading || selectedPlan === user.plan ? 0.6 : 1
          }}
        >
          {loading ? 'Processing...' : 'Change Plan'}
        </button>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(71, 85, 105, 0.5)',
            borderRadius: '16px',
            padding: '2rem',
            width: '100%',
            maxWidth: '400px',
            margin: '1rem'
          }}>
            <h2 style={{
              color: '#e5e7eb',
              fontSize: '1.25rem',
              fontWeight: 'bold',
              margin: 0,
              marginBottom: '1rem'
            }}>
              🔄 Confirm Plan Change
            </h2>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '0.5rem' 
              }}>
                <span style={{ color: '#9ca3af' }}>From:</span>
                <span style={{ color: currentPlan?.color || '#9ca3af' }}>
                  {currentPlan?.name}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#9ca3af' }}>To:</span>
                <span style={{ color: newPlan?.color || '#9ca3af' }}>
                  {newPlan?.name}
                </span>
              </div>
            </div>

            <div style={{ 
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              color: '#f59e0b',
              fontSize: '0.875rem'
            }}>
              ⚠️ This will immediately change the user's plan and may affect their points allocation.
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConfirmation(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'rgba(71, 85, 105, 0.5)',
                  color: '#9ca3af',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={confirmPlanChange}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanManagement;
